import { ScanDataObject, ThumbnailDataObject } from "@/typed/CaseStateData";
import { ModelLabel } from "@/typed/ModelTypes";
import { transactionClosed } from "@/utils/indexeddb";

const DB_NAME = "uploadsCacheDB";
const DB_VERSION = 3;
const STORE_NAME = "uploads";

const openDatabase = () => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (db.objectStoreNames.contains(STORE_NAME)) {
        db.deleteObjectStore(STORE_NAME);
      }

      db.createObjectStore(STORE_NAME);
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
};

interface ScanMetadata {
  id: string;
}

type StoredCaseScan = ScanMetadata & ScanDataObject;

interface CaseScans {
  scans?: string[];
}

interface ThumbnailMetadata {
  id?: string;
}

type StoredCaseThumbnail = ThumbnailMetadata & ThumbnailDataObject;
type StoredCaseThumbnails = StoredCaseThumbnail[];

interface CaseThumbnails {
  thumbnails?: ThumbnailDataObject[];
}

interface CaseMetadata {
  timestamp: number;
  uploadId: number;
  combinedThumbnail?: ThumbnailDataObject;
}

interface CaseData {
  combinedThumbnail?: StoredCaseThumbnail;
  scans: StoredCaseScan[];
}

type StoredCaseData = (CaseScans & CaseMetadata) | undefined;

type UploadCase = CaseData & CaseThumbnails;

const createScanId = (uploadId: number, index: number) => `scan-${uploadId}-${index}`;
const createThumbnailId = (uploadId: number, index: number) => `thumbnail-${uploadId}-${index}`;

const UploadToStoredCaseData = (upload: NewUploadObjectIndexedDB): StoredCaseData => ({
  scans: upload.scans.map((_, index) => createScanId(upload.uploadId, index)),
  timestamp: upload.timestamp,
  uploadId: upload.uploadId
});

const UploadToStoredCaseScans = (upload: NewUploadObjectIndexedDB): StoredCaseScan[] =>
  upload.scans.map((scan, index) => ({ ...scan, id: createScanId(upload.uploadId, index) }));

const UploadToStoredThumbnails = (upload: NewUploadObjectIndexedDB): StoredCaseThumbnails =>
  upload.scans?.map((thumbnail, index) => ({ data: null, type: null, id: createThumbnailId(upload.uploadId, index) })) ?? [];

const ScanIdToThumbnailId = (scanId: string) => scanId.replace(/^scan-/, "thumbnail-");

const StoredDataToCaseStateData = (data: StoredCaseData, scans: StoredCaseScan[], thumbnails: StoredCaseThumbnails): UploadCase => ({
  scans: scans,
  thumbnails: thumbnails
});

const NewUploadToDataKey = (upload: NewUploadObjectIndexedDB): string => UploadIdToDataKey(upload.uploadId);
const UploadIdToDataKey = (uploadId: number): string => `upload-${uploadId}`;

const getStoredUploadData = (dbStore: IDBObjectStore, uploadId: number) => {
  const caseDataRequest = dbStore.get(UploadIdToDataKey(uploadId));
  return new Promise<StoredCaseData | undefined>((resolve, reject) => {
    caseDataRequest.onsuccess = () => {
      const storeItem = caseDataRequest.result as StoredCaseData;
      resolve(storeItem);
    };
    caseDataRequest.onerror = () => reject(caseDataRequest.error);
  });
};

const getStoredScanData = async (dbStore: IDBObjectStore, scanId: string) => {
  return await new Promise<StoredCaseScan>((resolve, reject) => {
    const request = dbStore.get(scanId);
    request.onsuccess = () => {
      resolve(request.result as StoredCaseScan);
    };
    request.onerror = () => {
      reject(request.error);
    };
  });
};

const getStoredScansData = async (dbStore: IDBObjectStore, uploadId: number) => {
  const data = await getStoredUploadData(dbStore, uploadId);
  return await Promise.all(data?.scans?.map(async (scanId) => await getStoredScanData(dbStore, scanId)) ?? []);
};

const getStoredThumbnailData = async (dbStore: IDBObjectStore, thumbnailId: string) => {
  return await new Promise<StoredCaseThumbnail | undefined>((resolve) => {
    const request = dbStore.get(thumbnailId);
    request.onsuccess = () => {
      resolve(request.result as StoredCaseThumbnail);
    };
    request.onerror = () => {
      resolve(undefined);
    };
  });
};

const getStoredThumbnailFromScanID = async (dbStore: IDBObjectStore, scanId: string) => {
  return await getStoredThumbnailData(dbStore, ScanIdToThumbnailId(scanId));
};

const getStoredThumbnailsFromUpload = async (dbStore: IDBObjectStore, uploadId: number) => {
  const data = await getStoredUploadData(dbStore, uploadId);

  const thumbnails: StoredCaseThumbnails = (
    await Promise.all(data?.scans?.map(async (scanId) => await getStoredThumbnailFromScanID(dbStore, scanId)) ?? [])
  ).filter((thumbnail) => thumbnail !== undefined);

  if (data?.combinedThumbnail) {
    thumbnails.push(data?.combinedThumbnail);
  }

  return thumbnails;
};

export type NewUploadObjectIndexedDB = { scans: ScanDataObject[] } & CaseMetadata;
export type UploadObjectIndexedDB = UploadCase;

export const clearUploadsStore = async (): Promise<void> => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readwrite");
  const store = transaction.objectStore(STORE_NAME);

  await new Promise<void>((resolve, reject) => {
    const clearRequest = store.clear();

    clearRequest.onsuccess = () => {
      resolve();
    };

    clearRequest.onerror = (event) => {
      reject((event.target as IDBRequest).error);
    };
  });

  await transactionClosed(transaction);
};

export const addNewUploadToIndexedDB = async (dentalCase: NewUploadObjectIndexedDB): Promise<string[]> => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readwrite");
  const dbStore = transaction.objectStore(STORE_NAME);

  if (dentalCase?.combinedThumbnail !== undefined) {
    dentalCase.combinedThumbnail.label = "combined";
  }

  dbStore.put(UploadToStoredCaseData(dentalCase), NewUploadToDataKey(dentalCase));
  UploadToStoredThumbnails(dentalCase).forEach((thumbnail) => dbStore.put(thumbnail, thumbnail.id));
  const scans = UploadToStoredCaseScans(dentalCase);
  scans.forEach((scan) => dbStore.put(scan, scan.id));

  await transactionClosed(transaction);

  return scans.map((scan) => scan.id);
};

export const getThumbnailFromIndexedDB = async (scanId: string) => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readonly");
  const dbStore = transaction.objectStore(STORE_NAME);

  return await getStoredThumbnailFromScanID(dbStore, scanId);
};

export const getThumbnailsFromIndexedDB = async (uploadId: number): Promise<StoredCaseThumbnails> => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readonly");
  const dbStore = transaction.objectStore(STORE_NAME);

  return await getStoredThumbnailsFromUpload(dbStore, uploadId);
};

export const setThumbnailFromIndexedDB = async (scanId: string, thumbnail: ThumbnailDataObject) => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readwrite");
  const dbStore = transaction.objectStore(STORE_NAME);

  const id = ScanIdToThumbnailId(scanId);

  const data: StoredCaseThumbnail = {
    ...thumbnail,
    id
  };

  dbStore.put(data, id);

  await transactionClosed(transaction);
};

export const getCombinedThumbnailFromIndexedDB = async (uploadId: number) => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readonly");
  const dbStore = transaction.objectStore(STORE_NAME);

  const data = await getStoredUploadData(dbStore, uploadId);

  return data?.combinedThumbnail;
};

export const setCombinedThumbnailFromIndexedDB = async (uploadId: number, thumbnail: ThumbnailDataObject) => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readwrite");
  const dbStore = transaction.objectStore(STORE_NAME);

  let data = await getStoredUploadData(dbStore, uploadId);
  if (data === undefined) {
    data = {
      scans: undefined,
      timestamp: Date.now(),
      uploadId
    };
  }

  thumbnail.label = "combined";
  data.combinedThumbnail = thumbnail;

  dbStore.put(data, UploadIdToDataKey(uploadId));

  await transactionClosed(transaction);
};

export const getUploadScansFromIndexedDB = async (uploadId: number) => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readonly");
  const dbStore = transaction.objectStore(STORE_NAME);

  return await getStoredScansData(dbStore, uploadId);
};

export const removeAndAddScansFromIndexedDB = async (removeFrom: { [id: number]: string[] }, addTo?: number) => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readwrite");
  const dbStore = transaction.objectStore(STORE_NAME);

  const data = addTo !== undefined ? await getStoredUploadData(dbStore, addTo) : undefined;
  const scans: string[] = Object.values(removeFrom).flat();
  if (data !== undefined) {
    data.scans = (data.scans ?? []).concat(scans);
    dbStore.put(data, UploadIdToDataKey(data.uploadId));
  }

  for (const upload of Object.keys(removeFrom)) {
    const uploadId = Number(upload);
    const data = await getStoredUploadData(dbStore, uploadId);

    const removedScans = removeFrom[uploadId];
    if (data?.scans) {
      data.scans = data.scans.filter((scanId) => !removedScans.includes(scanId));
    }

    dbStore.put(data, UploadIdToDataKey(uploadId));
  }

  await transactionClosed(transaction);
};

export const classifyScanInIndexedDB = async (scanId: string, label: ModelLabel) => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readwrite");
  const dbStore = transaction.objectStore(STORE_NAME);

  const scan = await getStoredScanData(dbStore, scanId);

  scan.label = label;
  dbStore.put(scan, scanId);

  await transactionClosed(transaction);
};

export const getUploadModelFromIndexedDB = async (uploadId: number): Promise<UploadCase> => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readonly");
  const dbStore = transaction.objectStore(STORE_NAME);

  return (async (): Promise<UploadCase> => {
    const data = await getStoredUploadData(dbStore, uploadId);

    const thumbnails = await getStoredThumbnailsFromUpload(dbStore, uploadId);

    const scans = await getStoredScansData(dbStore, uploadId);

    return StoredDataToCaseStateData(data, scans, thumbnails);
  })();
};

export const deleteUploadFromStore = async (uploadId: number): Promise<void> => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readwrite");
  const store = transaction.objectStore(STORE_NAME);

  const data = await getStoredUploadData(store, uploadId);
  store.delete(UploadIdToDataKey(uploadId));

  data?.scans?.forEach((scanId) => {
    store.delete(scanId);
    store.delete(ScanIdToThumbnailId(scanId));
  });

  await transactionClosed(transaction);
};
